export * from "./constants";
export * from "./exchange";
export * from "./formatter";
export * from "./math";
export * from "./scale";
export * from "./service";
export * from "./types";

import { currencyCode } from "./constants";
import { ExchangeRateService } from "./exchange";
import { CurrencyFormatter } from "./formatter";
import { MoneyService } from "./service";
import type { CurrencyCode, MoneyOptions } from "./types";

const exchange = new ExchangeRateService( { base: currencyCode, rates: { USD: 1, CNY: 1, INR: 1, EUR: 1, CENT: 1 } } );

const formatter = new CurrencyFormatter();

export const money = new MoneyService( exchange, formatter );

/**
 * 通用金额格式化参数。
 *
 * - `currency` 表示传入金额的原始单位，默认使用包内默认币种。
 * - `targetCurrency` 表示最终展示的目标币种，不传时默认与 `currency` 一致。
 * - 其他字段沿用 `MoneyOptions`，用于控制精度、缩放、千分位、符号展示和汇率转换。
 *
 * @example
 * moneyFormatCurrency( 1200, { currency: "INR", precision: 2 } );
 *
 * @example
 * moneyFormatCurrency( 123, {
 *     currency: "CENT",
 *     targetCurrency: "USD",
 *     useExchange: true,
 * } );
 */
export type MoneyFormatCurrencyOptions = Omit<MoneyOptions, "currency" | "targetCurrency"> & {
    currency?: CurrencyCode;
    targetCurrency?: CurrencyCode;
};

/**
 * 底层通用金额格式化入口。
 *
 * 该方法统一处理“原始币种 -> 目标币种 -> 展示格式”的流程，业务侧优先使用更具语义的
 * `formatUnitPrice` 或 `formatMonetaryValue`，只有在确实需要自定义币种/目标币种时再直接调用。
 *
 * @param amount 需要格式化的原始金额。
 * @param moneyOptions 金额展示参数，包含原始币种、目标币种、精度、缩放、符号展示等配置。
 * @returns 按目标币种格式化后的金额文案。
 *
 * @example
 * moneyFormatCurrencyWith( 99, {
 *     currency: "CENT",
 *     targetCurrency: "CENT",
 *     useExchange: false,
 * } );
 *
 * @example
 * moneyFormatCurrencyWith( 2500, {
 *     currency: "CENT",
 *     targetCurrency: "USD",
 *     useExchange: true,
 *     precision: 2,
 * } );
 */
export function moneyFormatCurrencyWith( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    const { currency = currencyCode, targetCurrency = currency, ...formatOptions } = moneyOptions;

    return money.convertAndFormat( { amount: Number( amount ), currency }, targetCurrency, formatOptions );
}

/**
 * 通用金额格式化入口。
 *
 * 这是 `moneyFormatCurrencyWith` 的简化别名，用于保持业务代码调用语义统一。
 *
 * @param amount 需要格式化的原始金额。
 * @param moneyOptions 金额展示参数。
 * @returns 按目标币种格式化后的金额文案。
 *
 * @example
 * moneyFormatCurrency( 1000, { currency: "INR", useScale: false } );
 */
export function moneyFormatCurrency( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrencyWith( amount, moneyOptions );
}

/**
 * 格式化竞猜单价。
 *
 * 竞猜单价字段按 `CENT` 理解，并保持以 `CENT` 展示，不做 `CENT -> USD` 金额换算。
 * 适用于 `price`、`yesRealtimePrice`、`noRealtimePrice`、`unitPrice` 等“单价/赔率价格”字段。
 *
 * @param amount 原始单价，单位为 `CENT`。
 * @param moneyOptions 展示参数，可覆盖精度、符号展示、千分位等。
 * @returns 按美分单价格式化后的展示文案。
 *
 * @example
 * formatUnitPrice( 50 );
 *
 * @example
 * formatUnitPrice( 12.5, {
 *     precision: 1,
 *     showCurrencySymbol: true,
 * } );
 */
export function formatUnitPrice( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrency( amount, {
        currency: "CENT",
        targetCurrency: "CENT",
        precision: 0,
        useExchange: false,
        ...moneyOptions,
    } );
}

/**
 * 格式化真实金额。
 *
 * 总价、收益、亏损、持仓价值、成交量、手续费等金额字段按 `CENT` 理解，并转换成 `USD` 后展示。
 * 适用于 `tradeAmount`、`totalAmount`、`feeAmount`、`currentValue`、`profit` 等“金额/总价”字段。
 *
 * @param amount 原始金额，单位为 `CENT`。
 * @param moneyOptions 展示参数，可覆盖精度、缩放、千分位、符号展示等。
 * @returns 按美元金额格式化后的展示文案。
 *
 * @example
 * formatMonetaryValue( 123 );
 *
 * @example
 * formatMonetaryValue( 12345, {
 *     precision: 2,
 *     useScale: false,
 * } );
 */
export function formatMonetaryValue( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrency( amount, {
        currency: "CENT",
        targetCurrency: "USD",
        useExchange: true,
        ...moneyOptions,
    } );
}
